import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { 
  Building2, 
  Star, 
  ExternalLink,
  DollarSign,
  Calendar,
  Globe,
  Mail,
  Phone
} from 'lucide-react';
import SEO from '../components/SEO';

interface Sponsor {
  id: string;
  companyName: string;
  companyType: string;
  website?: string;
  email: string;
  phone?: string;
  address?: string;
  contactPerson: string;
  contactTitle?: string;
  contactEmail: string;
  contactPhone?: string;
  sponsorshipType: string;
  budget: string;
  duration: string;
  targetAudience?: string;
  expectedReach?: string;
  previousSponsorship?: string;
  marketingGoals?: string;
  specialRequirements?: string;
  additionalInfo?: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: any;
  updatedAt: any;
  userId: string;
  userEmail: string;
  userName: string;
}

const Sponsors: React.FC = () => {
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'approved' | 'pending' | 'rejected'>('all');

  useEffect(() => {
    loadSponsors();
  }, []);

  const loadSponsors = async () => {
    try {
      setLoading(true);
      const sponsorsQuery = query(
        collection(db, 'sponsors'), 
        orderBy('createdAt', 'desc')
      );
      const sponsorsSnapshot = await getDocs(sponsorsQuery);
      const sponsorsData = sponsorsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Sponsor[];
      
      setSponsors(sponsorsData);
      setError('');
    } catch (err) {
      console.error('Sponsorlar yüklenirken hata:', err);
      setError('Sponsorlar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const filteredSponsors = sponsors.filter(sponsor => {
    if (filterStatus === 'all') return true;
    return sponsor.status === filterStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'text-green-400 bg-green-500/20 border-green-500/30';
      case 'pending':
        return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
      case 'rejected':
        return 'text-red-400 bg-red-500/20 border-red-500/30';
      default:
        return 'text-gray-400 bg-gray-500/20 border-gray-500/30';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved':
        return 'Onaylandı';
      case 'pending':
        return 'Beklemede';
      case 'rejected':
        return 'Reddedildi';
      default:
        return 'Bilinmiyor';
    }
  };

  const getSponsorshipTypeText = (type: string) => {
    switch (type) {
      case 'ana':
        return 'Ana Sponsor';
      case 'altin':
        return 'Altın Sponsor';
      case 'gumus':
        return 'Gümüş Sponsor';
      case 'bronz':
        return 'Bronz Sponsor';
      case 'ozel':
        return 'Özel Sponsorluk';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-900 text-white pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Sponsorlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Sponsorlar - Aedon Community" 
        description="Aedon Community'nin değerli sponsorları. FiveM topluluğumuzu destekleyen şirketler ve kuruluşlar."
        keywords="sponsorlar, destekçiler, FiveM, topluluk, Aedon Community, sponsorluk"
      />
      
      <div className="min-h-screen bg-dark-900 text-white pt-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-gaming font-bold text-white mb-4">
              Sponsorlarımız
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Aedon Community'yi destekleyen değerli şirketler ve kuruluşlar
            </p>
          </motion.div>

          {/* Filter Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-4 mb-12"
          >
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                filterStatus === 'all'
                  ? 'bg-primary-600 text-white shadow-lg'
                  : 'bg-dark-700 text-gray-300 hover:bg-dark-600'
              }`}
            >
              Tümü ({sponsors.length})
            </button>
            <button
              onClick={() => setFilterStatus('approved')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                filterStatus === 'approved'
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-dark-700 text-gray-300 hover:bg-dark-600'
              }`}
            >
              Onaylanan ({sponsors.filter(s => s.status === 'approved').length})
            </button>
            <button
              onClick={() => setFilterStatus('pending')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                filterStatus === 'pending'
                  ? 'bg-yellow-600 text-white shadow-lg'
                  : 'bg-dark-700 text-gray-300 hover:bg-dark-600'
              }`}
            >
              Bekleyen ({sponsors.filter(s => s.status === 'pending').length})
            </button>
            <button
              onClick={() => setFilterStatus('rejected')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                filterStatus === 'rejected'
                  ? 'bg-red-600 text-white shadow-lg'
                  : 'bg-dark-700 text-gray-300 hover:bg-dark-600'
              }`}
            >
              Reddedilen ({sponsors.filter(s => s.status === 'rejected').length})
            </button>
          </motion.div>

          {/* Error Message */}
          {error && (
            <div className="text-center py-12">
              <div className="bg-red-900/20 border border-red-500 rounded-lg p-6 text-red-400">
                <p className="text-lg font-semibold mb-2">Hata Oluştu</p>
                <p>{error}</p>
              </div>
            </div>
          )}

          {/* Sponsors Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSponsors.map((sponsor, index) => (
              <motion.div
                key={sponsor.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl p-6 hover:border-primary-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-primary-500/10"
              >
                {/* Status Badge */}
                <div className="flex justify-between items-start mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(sponsor.status)}`}>
                    {getStatusText(sponsor.status)}
                  </span>
                  <span className="text-xs text-gray-400">
                    {sponsor.createdAt?.toDate ? 
                      sponsor.createdAt.toDate().toLocaleDateString('tr-TR') : 
                      'Tarih bilinmiyor'
                    }
                  </span>
                </div>

                {/* Company Info */}
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-white mb-2 flex items-center">
                    <Building2 className="w-5 h-5 mr-2 text-primary-400" />
                    {sponsor.companyName}
                  </h3>
                  <p className="text-gray-400 text-sm mb-2">{sponsor.companyType}</p>
                  <p className="text-gray-300 text-sm">{sponsor.contactPerson}</p>
                  {sponsor.contactTitle && (
                    <p className="text-gray-400 text-xs">{sponsor.contactTitle}</p>
                  )}
                </div>

                {/* Sponsorship Details */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center space-x-2">
                    <Star className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm text-gray-300">
                      {getSponsorshipTypeText(sponsor.sponsorshipType)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <DollarSign className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-gray-300">{sponsor.budget}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-gray-300">{sponsor.duration} Ay</span>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-400">{sponsor.contactEmail}</span>
                  </div>
                  {sponsor.phone && (
                    <div className="flex items-center space-x-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-400">{sponsor.phone}</span>
                    </div>
                  )}
                  {sponsor.website && (
                    <div className="flex items-center space-x-2">
                      <Globe className="w-4 h-4 text-gray-400" />
                      <a 
                        href={sponsor.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary-400 hover:text-primary-300 transition-colors flex items-center"
                      >
                        Website
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    </div>
                  )}
                </div>

                {/* Additional Info */}
                {sponsor.targetAudience && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-300 mb-1">Hedef Kitle</h4>
                    <p className="text-xs text-gray-400">{sponsor.targetAudience}</p>
                  </div>
                )}

                {sponsor.expectedReach && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-300 mb-1">Beklenen Erişim</h4>
                    <p className="text-xs text-gray-400">{sponsor.expectedReach}</p>
                  </div>
                )}

                {/* Marketing Goals Preview */}
                {sponsor.marketingGoals && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-300 mb-1">Pazarlama Hedefleri</h4>
                    <p className="text-xs text-gray-400 line-clamp-2">
                      {sponsor.marketingGoals.length > 100 
                        ? `${sponsor.marketingGoals.substring(0, 100)}...` 
                        : sponsor.marketingGoals
                      }
                    </p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Empty State */}
          {filteredSponsors.length === 0 && !loading && (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-400 mb-2">
                {filterStatus === 'all' ? 'Henüz sponsor yok' : 'Bu kategoride sponsor bulunamadı'}
              </h3>
              <p className="text-gray-500 mb-6">
                {filterStatus === 'all' 
                  ? 'Sponsorlar burada görünecek' 
                  : 'Farklı bir kategori seçmeyi deneyin'
                }
              </p>
              {filterStatus !== 'all' && (
                <button
                  onClick={() => setFilterStatus('all')}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300"
                >
                  Tüm Sponsorları Gör
                </button>
              )}
            </div>
          )}

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-16 text-center"
          >
            <div className="bg-gradient-to-r from-primary-500/10 to-primary-600/10 rounded-xl p-8 border border-primary-500/20">
              <h3 className="text-2xl font-bold text-white mb-4">
                Siz de Sponsor Olmak İster misiniz?
              </h3>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                Aedon Community'yi destekleyin ve markanızı FiveM topluluğunda tanıtın.
              </p>
              <a
                href="/sponsors/apply"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 rounded-full hover:scale-105 transition-all duration-300 transform hover:-translate-y-1"
              >
                <Building2 className="w-5 h-5 mr-3" />
                Sponsor Başvurusu Yap
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Sponsors;
